
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

interface CookingExperienceFieldsProps {
  yearsExperience: string;
  prepMethods: string[];
  kitchenNotes: string[];
  experienceTypes: string[];
  otherExperience: string;
  onYearsExperienceChange: (value: string) => void;
  onPrepMethodsChange: (value: string[]) => void;
  onKitchenNotesChange: (value: string[]) => void;
  onExperienceTypesChange: (value: string[]) => void;
  onOtherExperienceChange: (value: string) => void;
}

export const CookingExperienceFields = ({
  yearsExperience,
  prepMethods,
  kitchenNotes,
  experienceTypes,
  otherExperience,
  onYearsExperienceChange,
  onPrepMethodsChange,
  onKitchenNotesChange,
  onExperienceTypesChange,
  onOtherExperienceChange,
}: CookingExperienceFieldsProps) => {
  const prepMethodOptions = [
    "Smoked",
    "Oven-baked",
    "Air-fried",
    "Roasted",
    "Slow cooked",
    "Wood-fired",
    "Baked goods"
  ];

  const kitchenNoteOptions = [
    "Nut free",
    "Gluten free",
    "Dairy free",
    "Pets in house"
  ];

  const experienceTypeOptions = [
    "Home chef only",
    "Culinary school",
    "Restaurant experience",
    "Cottage kitchen",
    "Other"
  ];

  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="yearsExperience" className="text-black">Years of Cooking Experience</Label>
        <Input
          id="yearsExperience"
          type="number"
          value={yearsExperience}
          onChange={(e) => onYearsExperienceChange(e.target.value)}
          min="0"
          className="bg-[#EDEDED] border-gray-200 placeholder:text-[#6B6B6B]"
          placeholder="Enter years of experience"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-black">Food Prep Methods</Label>
        <div className="grid grid-cols-2 gap-2">
          {prepMethodOptions.map((method) => (
            <div key={method} className="flex items-center space-x-2">
              <Checkbox
                id={`prep-${method}`}
                checked={prepMethods.includes(method)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    onPrepMethodsChange([...prepMethods, method]);
                  } else {
                    onPrepMethodsChange(prepMethods.filter(m => m !== method));
                  }
                }}
              />
              <Label htmlFor={`prep-${method}`} className="text-sm text-black">
                {method}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-black">Kitchen Notes</Label>
        <div className="grid grid-cols-2 gap-2">
          {kitchenNoteOptions.map((note) => (
            <div key={note} className="flex items-center space-x-2">
              <Checkbox
                id={`note-${note}`}
                checked={kitchenNotes.includes(note)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    onKitchenNotesChange([...kitchenNotes, note]);
                  } else {
                    onKitchenNotesChange(kitchenNotes.filter(n => n !== note));
                  }
                }}
              />
              <Label htmlFor={`note-${note}`} className="text-sm text-black">
                {note}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-black">Types of Cooking Experience</Label>
        <div className="grid grid-cols-2 gap-2">
          {experienceTypeOptions.map((type) => (
            <div key={type} className="flex items-center space-x-2">
              <Checkbox
                id={`exp-${type}`}
                checked={experienceTypes.includes(type)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    onExperienceTypesChange([...experienceTypes, type]);
                  } else {
                    onExperienceTypesChange(experienceTypes.filter(t => t !== type));
                  }
                }}
              />
              <Label htmlFor={`exp-${type}`} className="text-sm text-black">
                {type}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {experienceTypes.includes("Other") && (
        <div>
          <Label htmlFor="otherExperience" className="text-black">Please specify other experience</Label>
          <Input
            id="otherExperience"
            value={otherExperience}
            onChange={(e) => onOtherExperienceChange(e.target.value)}
            className="bg-[#EDEDED] border-gray-200 placeholder:text-[#6B6B6B]"
            placeholder="Describe your other cooking experience"
          />
        </div>
      )}
    </div>
  );
};
